import React, { useEffect } from 'react';
/**
 * /llms and /llms.txt — machine-readable Periscan brief.
 *
 * Follows the spirit of the llms.txt proposal (https://llmstxt.org/):
 * a single, clean, markdown-style document that LLMs, AI crawlers, and
 * search engines can ingest to understand the site without scraping
 * animation-heavy React UI.
 *
 * Rendered as semantic HTML inside a single <article> with one <h1> and
 * logical <h2> sections, no animations, no JS-dependent content. The
 * markdown-style text is ALSO embedded as a single <pre> block at the
 * bottom for any crawler that prefers plaintext.
 */
const PERISCAN_LLMS_DOC = `# Periscan

> The Hacker On Your Side.
> Find the path. Validate the risk. Prove it's fixed.

Periscan is a self-service Automated Security Validation platform. It validates exposure, controls, attack paths, AI applications, and fixes — then turns the results into proof your team can use.

URL: https://periscan.com/
H1: Prove what can actually compromise you.
Tagline: The Hacker On Your Side.
Mantra: Find the path. Validate the risk. Prove it's fixed.
Category: Self-service Automated Security Validation (ASV / AEV / BAS / CTEM / EASM / Attack-path validation)
Founded: 2004 by Sean Heiney. Original company acquired by Catbird Networks. Reborn 2026.

## What Periscan Is

Periscan is self-service Automated Security Validation. It runs safe validation missions across your environment and turns the results into evidence — what is real, what is blocked, what is missed, what should be fixed, and whether the fix worked.

Periscan answers the questions security teams still cannot answer with scanners, EDR, SIEM, dashboards, tickets, compliance screenshots, or pentest PDFs alone:

- What can actually hurt us?
- Which control failed?
- Which path matters most?
- What should we fix first?
- Did the fix work?
- Can we prove it?

## What Periscan Is Not

- Not a vulnerability scanner. Scanners show what might be wrong. Periscan validates what is real.
- Not a pentest replacement. Pentests are point-in-time. Periscan is continuous.
- Not another dashboard. Dashboards report what tools see. Periscan validates what they do.
- Not "AI magic." AI helps where it actually helps: choosing what to test, connecting signals, explaining paths, recommending fixes, re-testing remediation, preparing evidence. The validation still has to prove itself.

## One Platform. Five Validation Jobs.

1. **Validate exposure.** See which exposures are reachable, exploitable, blocked, detected, mitigated, or not worth panic.
2. **Test controls.** Check whether defenses detect, block, log, alert, and route the activity they are supposed to catch.
3. **Find attack paths.** Connect cloud, identity, SaaS, code, secrets, AI apps, and internal systems into realistic paths to impact.
4. **Verify fixes.** Re-test after remediation. A closed ticket is not a closed risk.
5. **Produce evidence.** Generate proof for security teams, executives, customers, auditors, insurers, MSSPs, and boards.

## How It Works — Map. Simulate. Validate. Fix. Prove.

1. **Map.** Map your critical assets, identities, applications, AI systems, and existing security controls. Periscan connects to cloud, identity, SaaS, code, AI apps, security tools, ticketing systems, and optional internal runners. It builds an evidence model of assets, identities, permissions, controls, exposures, applications, and remediation history.

2. **Simulate.** Run safe, real-world validation missions continuously. Missions cover exposure, controls, attack paths, AI applications, and fixes. Testing is scoped, authorized, policy-controlled, and designed to avoid destructive behavior.

3. **Validate.** Prove whether defenses detect, block, log, alert, and respond. Did EDR see it? Did SIEM log it? Did MFA stop it? Did the WAF block it? Did the SOC or MDR get the alert?

4. **Prioritize.** Focus remediation on paths with real business impact. Periscan separates real risk from noise, identifies the paths that matter, explains why they matter, and recommends the fixes that reduce the most risk.

5. **Prove.** Re-test fixes and generate evidence you can share. Periscan turns validation results into evidence packs for security teams, executives, customers, auditors, insurers, vCISOs, and MSSPs. If the risk is fixed, you get proof. If it is not, you get the truth.

## Periscan Operators (AI that does the validation work)

Not chatbots. Not magic. Scoped security operators with policy, evidence, and proof. They operate inside customer-approved scope with policy controls and evidence requirements.

- **Red Team Operator** — Builds safe attack scenarios and validates realistic paths.
- **Blue Team Operator** — Checks whether controls detect, block, log, alert, and route correctly.
- **Exposure Operator** — Finds which exposures are real, reachable, exploitable, blocked, or noise.
- **Remediation Operator** — Turns validated risk into fix plans, tickets, and re-tests.
- **Evidence Operator** — Creates proof for customers, auditors, insurers, boards, and executives.
- **AI App Security Operator** — Validates AI apps, RAG systems, copilots, agents, tools, and guardrails.

The operators do the repetitive work. Your team keeps control.

## Validation Snapshot (the first proof moment)

The first mile of security validation should not require a procurement cycle. Periscan can start with a focused Validation Snapshot that validates your top exposure paths, control observations, AI app risks, and remediation priorities.

Snapshot outputs:
- What can hurt us?
- What caught it?
- What missed it?
- What should we fix first?
- How do we prove it is fixed?

No giant deployment. No annual pentest wait. No 500 findings to decode. Just the first proof moment.

## AI Application Validation

Your AI app is an attack surface now. Periscan validates AI apps, RAG systems, copilots, agents, and tool-calling workflows.

- Prompt injection
- RAG authorization failures
- Sensitive data leakage
- Unsafe tool invocation
- Agent over-permissioning
- System prompt exposure
- Cross-tenant retrieval
- Guardrail drift
- AI security review evidence

"Trust the prompt" is not a control.

## Evidence

Periscan produces evidence packs for security teams, GRC, customers, auditors, cyber insurers, boards, vCISOs, MSSPs, and AI governance teams.

Report types:
- Executive risk summary
- Customer security review pack
- Cyber-insurance evidence
- SOC 2 / ISO support
- BAS / control validation report
- AI security validation report
- CTEM program summary
- MSSP client QBR
- Technical appendix

Proof, context, and next steps. Not an 80-page PDF of doom.

## Use Cases

- **CTEM validation** — Turn exposure management from a dashboard into a working loop.
- **BAS-lite control checks** — Test whether controls detect, block, log, and alert.
- **Attack-path validation** — Find the paths that lead to business impact. Break those first.
- **AI app validation** — Validate LLM apps, RAG systems, copilots, agents, and AI workflows.
- **Fix verification** — Re-test after remediation. Stop pretending ticket closure is security.
- **Customer security reviews** — Generate evidence customers can read without a decoder ring.
- **Cyber insurance** — Show validation activity, control checks, remediation history, and risk reduction.
- **MSSP delivery** — Run repeatable validation across clients without hiring a red team for every account.

## Who It Is For

- Security teams — validate exposure, controls, and remediation.
- SecOps teams — test detections, tune controls, prove response workflows.
- GRC teams — generate evidence for audits, insurance, and customer reviews.
- Red and purple teams — scale repeatable validation without doing everything by hand.
- MSSPs and vCISOs — deliver recurring validation across clients.
- AI product teams — validate AI apps before customers ask harder questions.

## Trust and Safety

Periscan is built around verified scope, customer authorization, policy controls, rate limits, audit logs, no destructive actions, and no real data exfiltration. Self-service does not mean reckless. Adversarial enough to be useful. Controlled enough to be allowed near production.

## Brand Legacy

Founded in 2004 by Sean Heiney, Periscan was built around a simple idea: security assessment should be continuous. By 2005, the original Periscan described its service as working constantly to give customers a "hacker's-eye view" of their network — before the market had acronyms for everything.

Today, the same idea has names: ASV, AEV, BAS, CEM, CTEM, EASM, attack-path validation. Different acronyms. Same direction. Security moved from point-in-time testing to continuous proof.

The original company was acquired by Catbird Networks. The brand eventually went quiet — but the thesis stayed alive. Now Periscan is being rebuilt by the same founder for the AI era.

The mission did not change. The surface did.

## Pricing

Periscan uses SaaS-style pricing based on what you validate: validated assets, identities, control sources, AI apps and workflows, internal runners, evidence workflows, and MSSP client tenants. No forced enterprise bundle. No opaque services-first slab. No "talk to sales" just to learn if you can afford the thing.

Start with a Validation Snapshot. Expand into continuous exposure validation, control validation, AI security validation, fix verification, and evidence packs. Final pricing is shaped during early access.

## Frequently Asked Questions

**What is Periscan?**
Periscan is a self-service Automated Security Validation platform. It helps teams validate exposure, controls, attack paths, AI applications, and remediation outcomes.

**How does Periscan work?**
Periscan connects to cloud, identity, SaaS, code, AI apps, security tools, ticketing systems, and optional internal runners. It builds an evidence model, runs validation missions, tests exposure and controls, recommends fixes, re-tests remediation, and generates proof.

**Is Periscan a vulnerability scanner?**
No. Periscan may use vulnerability data, but it is not a scanner dashboard. Periscan validates which exposures matter and whether the risk is fixed.

**Is Periscan BAS?**
Partly. Periscan includes BAS-lite control validation, but it also validates exposure, attack paths, AI apps, remediation, and evidence.

**Is Periscan AEV?**
Yes. Periscan is built for adversarial exposure validation: proving whether attack scenarios, exposures, and control gaps are real in your environment.

**Where does AI fit?**
AI helps Periscan understand context, choose what to validate, interpret results, recommend fixes, and prepare evidence. It is not a magic button. The validation still has to prove itself.

**What are Periscan Operators?**
Periscan Operators are AI-assisted validation workflows for red-team testing, blue-team validation, exposure analysis, remediation, evidence, and AI app security. They operate inside customer-approved scope with policy controls and evidence requirements.

**What is a Validation Snapshot?**
A focused first report. It shows top validated exposures, attack paths, control observations, remediation steps, and verification guidance.

**How does Periscan connect to my environment?**
Start with API connectors and external validation. Add an internal runner when you want deeper inside-out validation. No inbound firewall rules are required for the runner.

**Is it safe?**
Periscan is built around verified scope, customer authorization, policy controls, rate limits, audit logs, no destructive actions, and no real data exfiltration. Self-service does not mean reckless.

**How is pricing structured?**
Periscan uses SaaS-style pricing based on what you validate. Validated assets. Identities. Control sources. AI apps. Internal runners. Evidence workflows. MSSP client tenants. Final pricing is shaped during early access.

**When is Periscan available?**
Periscan is preparing for early access in 2026. Request access if you want to help shape the product before general availability.

## Contact

- Website: https://periscan.com/
- Early access: https://periscan.com/early-access
- Jobs: https://periscan.com/jobs
- Email: contact@periscan.com

## License

This document is provided for AI and crawler ingestion. The Periscan brand, content, and trademarks are © Periscan. Content may be quoted with attribution.
`;
export function LLMs() {
  useEffect(() => {
    document.title = 'Periscan · Machine-readable brief (llms.txt)';
  }, []);
  return <article style={{
    maxWidth: 760,
    margin: '0 auto',
    padding: '4rem 1.5rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    color: '#e5e7eb',
    background: '#050608',
    lineHeight: 1.6,
    fontSize: 14
  }} data-id="element-1660">
      <header style={{
      marginBottom: 32
    }} data-id="element-1661">
        <p style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.2em',
        color: '#1a8fd4',
        marginBottom: 8
      }} data-id="element-1662">
          /llms.txt · Machine-readable brief
        </p>
        <h1 style={{
        fontSize: 32,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: '#fff',
        marginBottom: 16
      }} data-id="element-1663">
          Periscan
        </h1>
        <p style={{
        color: '#9ca3af',
        marginBottom: 8
      }} data-id="element-1664">
          A clean, structured Periscan brief for LLMs, AI crawlers, and search
          engines. Renders identically with or without JavaScript. Follows the{' '}
          <a href="https://llmstxt.org/" style={{
          color: '#1a8fd4',
          textDecoration: 'underline'
        }} rel="noopener noreferrer" data-id="element-1665">
            llms.txt
          </a>{' '}
          proposal.
        </p>
        <p style={{
        color: '#6b7280',
        fontSize: 12
      }} data-id="element-1666">
          Human readers can view the full site at{' '}
          <a href="/" style={{
          color: '#1a8fd4',
          textDecoration: 'underline'
        }} data-id="element-1667">
            periscan.com
          </a>
          .
        </p>
      </header>

      {/* Raw markdown — the canonical machine-readable form */}
      <pre style={{
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      color: '#d1d5db',
      background: '#0a0d12',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 8,
      padding: 24,
      fontSize: 13,
      lineHeight: 1.7,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
    }} data-id="element-1668">
        {PERISCAN_LLMS_DOC}
      </pre>

      <footer style={{
      marginTop: 32,
      paddingTop: 24,
      borderTop: '1px solid rgba(255,255,255,0.06)',
      color: '#6b7280',
      fontSize: 12
    }} data-id="element-1669">
        <p style={{
        marginBottom: 4
      }} data-id="element-1670">
          Last updated: {new Date().toISOString().slice(0, 10)}
        </p>
        <p data-id="element-1671">
          Periscan · Self-service Automated Security Validation ·{' '}
          <a href="/" style={{
          color: '#1a8fd4',
          textDecoration: 'underline'
        }} data-id="element-1672">
            periscan.com
          </a>
        </p>
      </footer>
    </article>;
}
export { PERISCAN_LLMS_DOC };